import { useHistory, useLocation } from '@fs/zion-router'
import { Button, Cell, Grid, Select, Separator, TextField } from '@fs/zion-ui'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { getCtrsByOdmId } from '../../../services/templeService'
import { getLastPathElement } from '../../../util/StringUtil'
import { CtrLink } from '../../app/Links'
import DataView from '../../common/DataView'

export default function OdmSearch() {
  usePageView({ page_detail: 'DMConsole:OdmSearch' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.search.odm'))
  const { error } = useNotifications()
  const [id, setId] = useState('')
  const [odmType, setOdmType] = useState('')
  const [searchResults, setSearchResults] = useState()
  const history = useHistory()
  const location = useLocation()

  const handleCtrSearch = useCallback(
    (searchType, searchId) => {
      setSearchResults({})
      if (searchId && searchId !== '') {
        getCtrsByOdmId(searchType, searchId)
          .then((response) => {
            setSearchResults(response)
          })
          .catch((err) => {
            error(`Error getting odm search results: ${err.message || ''}`, err)
          })
      }
    },
    [error]
  )

  useEffect(() => {
    if (location.search && location.search !== '') {
      const searchParams = new URLSearchParams(location.search)

      const typeParam = searchParams.get('odmType')
      if (typeParam && typeParam !== '') {
        setOdmType(typeParam)
      }
      const idParam = searchParams.get('id')
      if (idParam && idParam !== '') {
        setId(idParam)
        handleCtrSearch(typeParam, idParam)
      }
    }
  }, [location.search, handleCtrSearch])

  const handleFormSubmit = (event) => {
    event.preventDefault()
    let queryString = `id=${id}`
    if (odmType && odmType !== '') {
      queryString += `&odmType=${odmType}`
    } else {
      queryString += `&odmType=ODM-P`
    }
    history.push(`/search/odm?${queryString}`)
  }

  function getPrincipal(persons) {
    return persons.find((person) => person.principal)
  }

  const collectionTypes = {
    1: 'ODM-C',
    5: 'ODM-G',
    6: 'ODM-F',
    7: 'ODM-P',
  }

  const idInput = (value) => {
    setOdmType(collectionTypes[value[0]])
    setId(value)
  }

  return (
    <>
      <form onSubmit={(event) => handleFormSubmit(event)}>
        <Grid>
          <Cell columns={2}>
            <TextField
              id="odmIdInput"
              label={t('odm.id', 'ODM ID')}
              value={id}
              onChange={(event) => idInput(event.target.value)}
              autoFocus
            />
          </Cell>
          <Cell columns={2}>
            <Select
              id="odmTypeInput"
              label={t('odm.type', 'ODM Type')}
              placeholder={t('odm.selectType', 'Select Type')}
              value={odmType}
              onChange={(event) => setOdmType(event.target.value)}
              options={[
                { value: 'ADE', label: t('odm.type.ade', 'ADE') },
                { value: 'ODM-C', label: t('odm.type.odm-c', 'CO') },
                { value: 'ODM-P', label: t('odm.type.odm-p', 'Pre-70') },
                { value: 'ODM-G', label: t('odm.type.odm-g', 'Giant') },
                { value: 'ODM-F', label: t('odm.type.odm-f', 'Family Entry') },
                { value: 'ODMIC', label: t('odm.type.odmic', 'ODMi Chinese') },
                { value: 'ODMIK', label: t('odm.type.odmik', 'ODMi Korean') },
                { value: 'ODMIJ', label: t('odm.type.odmij', 'ODMi Japanese') },
                { value: 'ODMIF', label: t('odm.type.odmif', 'ODMi Cyrillic') },
              ]}
            />
          </Cell>
          <p columns={8} />
        </Grid>
        <Button type="submit" emphasis="high" disabled={!id || id === '' || odmType === ''}>
          {t('action.search', 'Search')}
        </Button>
      </form>
      <Separator size="xs" />
      {searchResults && searchResults.length >= 0 && (
        <>
          <h4>
            {t(
              searchResults.length > 1 ? 'odm.search.matchesFound_plural' : 'odm.search.matchesFound',
              `${searchResults.length} Matches Found`,
              {
                count: searchResults.length,
              }
            )}
          </h4>
          {searchResults.map((result) => {
            return (
              <Fragment key={result.ctrs[0].id}>
                <DataView
                  data={[
                    {
                      label: t('ctrId', 'CTR ID'),
                      detail: <CtrLink ctrId={result.ctrs[0].id} />,
                    },
                    {
                      label: t('principalName', 'Principal Name'),
                      detail: getPrincipal(result.persons).names[0].nameForms[0].fullText,
                    },
                    {
                      label: t('ordinances.headers.performedDate', 'Performed Date'),
                      detail: result.ctrs[0].performedDate ? result.ctrs[0].performedDate.original : '',
                    },
                    {
                      label: t('type', 'Type'),
                      detail: getLastPathElement(result.ctrs[0].ordinanceType),
                    },
                    {
                      label: t('status.label', 'Status'),
                      detail: getLastPathElement(result.ctrs[0].ordinanceStatus),
                    },
                  ]}
                />
                <Separator size="nano" />
              </Fragment>
            )
          })}
        </>
      )}
    </>
  )
}
